/**
 * Import PO lines from PDF
 *
 * For POs entered after the fact: upload the PO document we sent the
 * manufacturer, read its line items, match each to a SKU, and set the PO's
 * quantities (and prices / product numbers when printed) to what the PDF says.
 * Nothing changes until "Apply" — and the PO still has to be saved after.
 */

import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InventoryService, { PoPdfExtraction, PoPdfLine } from "services/InventoryService";
import { POLineItem } from "models/InventoryModel";

type InventoryOption = { id: string; name: string | null; skus: string[] };

interface ImportPoPdfDialogProps {
  open: boolean;
  onClose: () => void;
  poNumber: string;
  inventoryOptions: InventoryOption[];
  currentLines: POLineItem[];
  /** SKU master price for an item on this PO's container type */
  catalogPriceFor: (inventoryId?: string | null) => number | undefined;
  suggestedProductNumberFor: (inventoryId?: string | null) => string | undefined;
  /** Replaces the editor's line list with the merged result; skusChanged = items were (re)matched from the PDF */
  onApply: (lines: POLineItem[], options: { skusChanged: boolean; poNumber: string | null; poDate: string | null }) => void;
  /** Creating a brand-new PO from the PDF (no PO number to compare against yet) */
  newPo?: boolean;
  /** A PDF dropped somewhere else (e.g. on the PO list) — read it as soon as the dialog opens */
  initialFile?: File | null;
}

/** "prices": keep the PO's items, take prices/quantities from the PDF. "skus": re-match items from the PDF. */
type ImportMode = "prices" | "skus";

export default function ImportPoPdfDialog({
  open,
  onClose,
  poNumber,
  inventoryOptions,
  currentLines,
  catalogPriceFor,
  suggestedProductNumberFor,
  onApply,
  newPo = false,
  initialFile = null,
}: ImportPoPdfDialogProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const takeFile = (files: FileList | null) => {
    const picked = files?.[0] ?? null;
    if (!picked) return;
    if (picked.type !== "application/pdf" && !picked.name.toLowerCase().endsWith(".pdf")) {
      setErrorMessage(`${picked.name} is not a PDF`);
      return;
    }
    setErrorMessage(null);
    setFile(picked);
  };
  const [isReading, setIsReading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<PoPdfExtraction | null>(null);
  // Per PDF line: the inventory id the person settled on (pre-filled from a decisive match)
  const [matches, setMatches] = useState<Record<number, string | null>>({});
  const hasExistingLines = currentLines.some(li => li.inventoryId && li.quantity > 0);
  const [mode, setMode] = useState<ImportMode>(hasExistingLines ? "prices" : "skus");
  const [applyPrices, setApplyPrices] = useState(true);
  const [applyQuantities, setApplyQuantities] = useState(true);

  const optionById = useMemo(() => new Map(inventoryOptions.map(opt => [opt.id, opt])), [inventoryOptions]);
  const currentByInventoryId = useMemo(
    () => new Map(currentLines.filter(li => li.inventoryId).map(li => [String(li.inventoryId), li])),
    [currentLines],
  );

  const reset = () => {
    setFile(null);
    setExtraction(null);
    setMatches({});
    setErrorMessage(null);
    setMode(hasExistingLines ? "prices" : "skus");
  };

  const handleRead = async (chosenFile: File | null = file) => {
    if (!chosenFile) return;
    setIsReading(true);
    setErrorMessage(null);
    try {
      const result = await service.extractPoPdfLines(poNumber, chosenFile);
      setExtraction(result);
      const initial: Record<number, string | null> = {};
      result.lines.forEach((line, index) => {
        initial[index] = line.decisive && line.suggestions[0] ? String(line.suggestions[0].itemId) : null;
      });
      setMatches(initial);
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsReading(false);
    }
  };

  // A file dropped on the PO list arrives with the dialog already open: read it straight away.
  useEffect(() => {
    if (!open || !initialFile || file || extraction || isReading) return;
    setFile(initialFile);
    handleRead(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile]);

  // The server names every item it suggests ("SKU — name"). Kept as a fallback so a row never
  // shows a bare inventory id when the page's own item list happens to lack that item.
  const serverLabelById = useMemo(() => {
    const labels = new Map<string, string>();
    for (const line of extraction?.lines ?? []) {
      for (const suggestion of line.suggestions) labels.set(String(suggestion.itemId), suggestion.label);
    }
    return labels;
  }, [extraction]);

  const labelFor = (inventoryId: string | null | undefined): string => {
    if (!inventoryId) return "";
    const name = optionById.get(inventoryId)?.name
      ?? currentByInventoryId.get(inventoryId)?.inventoryName
      ?? serverLabelById.get(inventoryId);
    const productNumber = suggestedProductNumberFor(inventoryId);
    return `${inventoryId}${name ? ` - ${name}` : ""}${productNumber ? ` · ${productNumber}` : ""}`;
  };

  // The merged line list: every matched PDF line at the PDF's quantity, plus
  // current lines the PDF does not mention (left untouched, listed below).
  const plan = useMemo(() => {
    if (!extraction) return null;
    const matched = new Map<string, { line: PoPdfLine; index: number }>();
    const duplicates = new Set<string>();
    extraction.lines.forEach((line, index) => {
      const id = matches[index];
      if (!id) return;
      if (matched.has(id)) duplicates.add(id);
      matched.set(id, { line, index });
    });
    const merged: POLineItem[] = [];
    for (const [id, { line }] of matched) {
      const existing = currentByInventoryId.get(id);
      merged.push({
        ...(existing ?? { inventoryId: id, quantity: 0 }),
        inventoryId: id,
        inventoryName: existing?.inventoryName ?? optionById.get(id)?.name ?? undefined,
        quantity: line.quantity,
        // A printed price wins; otherwise keep what the line has, else the SKU master's
        unitPrice: line.unitPrice ?? existing?.unitPrice ?? catalogPriceFor(id) ?? null,
        manufacturerProductNumber: line.productNumber ?? existing?.manufacturerProductNumber ?? suggestedProductNumberFor(id) ?? null,
      });
    }
    const untouched = currentLines.filter(li => li.inventoryId && !matched.has(String(li.inventoryId)));
    const unmatchedCount = extraction.lines.filter((_, index) => !matches[index]).length;
    return { merged: [...merged, ...untouched], untouched, duplicates, unmatchedCount };
  }, [extraction, matches, currentByInventoryId, currentLines, optionById, catalogPriceFor, suggestedProductNumberFor]);

  // Keep-my-SKUs mode: each PDF line is paired with the PO line carrying the same
  // Danican product number (typed on the line, else the SKU master's for its item).
  // Items never change here; only prices and quantities move.
  const pricePlan = useMemo(() => {
    if (!extraction) return null;
    const norm = (value: string | null | undefined) => (value || "").trim().toUpperCase();
    const lineIndexByProductNumber = new Map<string, number>();
    currentLines.forEach((li, index) => {
      if (!li.inventoryId) return;
      const number = norm(li.manufacturerProductNumber) || norm(suggestedProductNumberFor(li.inventoryId));
      if (number && !lineIndexByProductNumber.has(number)) lineIndexByProductNumber.set(number, index);
    });
    const rows = extraction.lines.map(pdf => {
      const index = pdf.productNumber ? lineIndexByProductNumber.get(norm(pdf.productNumber)) : undefined;
      const poLine = index != null ? currentLines[index] : null;
      return {
        pdf,
        index: index ?? null,
        poLine,
        priceChanges: poLine != null && pdf.unitPrice != null && pdf.unitPrice !== (poLine.unitPrice ?? null),
        quantityChanges: poLine != null && pdf.quantity !== poLine.quantity,
      };
    });
    const matchedIndexes = new Set(rows.map(r => r.index).filter((i): i is number => i != null));
    const merged = currentLines.map((li, index) => {
      const row = rows.find(r => r.index === index);
      if (!row) return li;
      return {
        ...li,
        ...(applyPrices && row.pdf.unitPrice != null && { unitPrice: row.pdf.unitPrice }),
        ...(applyQuantities && { quantity: row.pdf.quantity }),
        manufacturerProductNumber: li.manufacturerProductNumber || row.pdf.productNumber || null,
      };
    });
    return {
      rows,
      merged,
      unmatched: rows.filter(r => r.index == null),
      untouched: currentLines.filter((li, index) => li.inventoryId && !matchedIndexes.has(index)),
      priceChangeCount: rows.filter(r => r.priceChanges).length,
      quantityChangeCount: rows.filter(r => r.quantityChanges).length,
    };
  }, [extraction, currentLines, suggestedProductNumberFor, applyPrices, applyQuantities]);

  const handleApply = () => {
    const header = { poNumber: extraction?.poNumber ?? null, poDate: extraction?.poDate ?? null };
    if (mode === "prices") {
      if (!pricePlan) return;
      onApply(pricePlan.merged, { skusChanged: false, ...header });
    } else {
      if (!plan) return;
      onApply(plan.merged, { skusChanged: true, ...header });
    }
    reset();
    onClose();
  };

  const canApply = mode === "prices"
    ? pricePlan != null && pricePlan.rows.some(r => r.index != null) && (applyPrices || applyQuantities)
    : plan != null && plan.merged.length > 0 && plan.duplicates.size === 0;
  const money = (value: number | null | undefined) => (value != null ? `$${value.toFixed(2)}` : "—");

  return (
    <Dialog open={open} onClose={isReading ? undefined : () => { reset(); onClose(); }} maxWidth="lg" fullWidth>
      <DialogTitle>{newPo ? "Create a PO from its PDF" : `Match ${poNumber} to its PO PDF`}</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}

        {!extraction ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {newPo
                ? "Upload the PO document that was sent to the manufacturer. Its PO number, date and line items are read and matched to SKUs by product number or description; you confirm each match, then fill in the rest of the PO before saving."
                : "Upload the PO document that was sent to the manufacturer. Its line items are read and matched to SKUs by product number or description; you confirm each match before anything changes."}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              hidden
              onChange={(e) => { takeFile(e.target.files); e.target.value = ""; }}
            />
            <Box
              onClick={() => { if (!isReading) fileInputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); if (!isReading) setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragOver(false); if (!isReading) takeFile(e.dataTransfer.files); }}
              sx={{
                border: "2px dashed",
                borderColor: isDragOver ? "primary.main" : "#CBD5E1",
                backgroundColor: isDragOver ? "#EEF2FF" : "#F8FAFC",
                borderRadius: 2, p: 3, textAlign: "center", cursor: isReading ? "default" : "pointer",
              }}
            >
              <PictureAsPdfIcon sx={{ color: file ? "primary.main" : "#64748B" }} />
              <Typography variant="body2" sx={{ fontWeight: file ? 600 : 400 }}>
                {file ? file.name : "Drop the PO PDF here, or click to choose it"}
              </Typography>
              {file && (
                <Typography variant="caption" color="text.secondary">Drop or click to pick a different file</Typography>
              )}
            </Box>
            {isReading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
                <CircularProgress size={20} />
                <Typography>Reading the PDF… this takes up to a minute</Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Read {extraction.lines.length} line{extraction.lines.length === 1 ? "" : "s"}
              {extraction.poNumber ? ` from PO ${extraction.poNumber}` : ""}
              {extraction.poDate ? ` dated ${extraction.poDate}` : ""}.
              {!newPo && extraction.poNumber && extraction.poNumber.trim().toUpperCase() !== poNumber.trim().toUpperCase() && (
                <> <strong>The PDF says PO {extraction.poNumber}, not {poNumber} — check it is the right document.</strong></>
              )}
            </Alert>

            {hasExistingLines && (
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as ImportMode)} sx={{ mb: 2 }}>
                <FormControlLabel
                  value="prices"
                  control={<Radio size="small" />}
                  label={<span><strong>Keep the SKUs on this PO</strong> — only update prices and quantities, paired by Danican product #</span>}
                />
                <FormControlLabel
                  value="skus"
                  control={<Radio size="small" />}
                  label={<span><strong>Re-match SKUs from the PDF</strong> — you confirm every item; use this when the PO's items may be wrong</span>}
                />
              </RadioGroup>
            )}

            {mode === "prices" && pricePlan ? (
              <>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={applyPrices} onChange={(e) => setApplyPrices(e.target.checked)} />}
                    label={`Update prices (${pricePlan.priceChangeCount} differ)`}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={applyQuantities} onChange={(e) => setApplyQuantities(e.target.checked)} />}
                    label={`Update quantities (${pricePlan.quantityChangeCount} differ)`}
                  />
                </Box>
                {pricePlan.unmatched.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {pricePlan.unmatched.length} PDF line{pricePlan.unmatched.length === 1 ? " has" : "s have"} no PO line with the same
                    Danican # and will be skipped: {pricePlan.unmatched.map(r => r.pdf.productNumber || r.pdf.description).join(", ")}.
                    Switch to "Re-match SKUs" to add them.
                  </Alert>
                )}
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Danican #</TableCell>
                      <TableCell>PO line (kept)</TableCell>
                      <TableCell align="right">Qty PO → PDF</TableCell>
                      <TableCell align="right">Price PO → PDF</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricePlan.rows.map((row, i) => (
                      <TableRow key={i} sx={row.poLine ? undefined : { opacity: 0.5 }}>
                        <TableCell>
                          {row.pdf.productNumber || "—"}
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{row.pdf.description}</Typography>
                        </TableCell>
                        <TableCell>{row.poLine ? labelFor(row.poLine.inventoryId) : "no matching PO line"}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: row.quantityChanges && applyQuantities ? 700 : 400 }}>
                          {row.poLine ? (row.quantityChanges ? `${row.poLine.quantity} → ${row.pdf.quantity}` : row.pdf.quantity) : row.pdf.quantity}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: row.priceChanges && applyPrices ? 700 : 400 }}>
                          {row.poLine
                            ? (row.priceChanges ? `${money(row.poLine.unitPrice)} → ${money(row.pdf.unitPrice)}` : money(row.pdf.unitPrice))
                            : money(row.pdf.unitPrice)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {pricePlan.untouched.length > 0 && (
                  <Alert severity="warning">
                    <strong>Not on the PDF, left as they are:</strong> {pricePlan.untouched.map(li => li.inventoryName || li.inventoryId).join(", ")}.
                  </Alert>
                )}
              </>
            ) : (
            <>
            {plan && (plan.unmatchedCount > 0 || plan.duplicates.size > 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {plan.unmatchedCount > 0 && (
                  <>{plan.unmatchedCount} line{plan.unmatchedCount === 1 ? " has" : "s have"} no SKU yet — pick one, or leave it out. </>
                )}
                {plan.duplicates.size > 0 && (
                  <strong>Two PDF lines point at the same SKU — fix that before applying.</strong>
                )}
              </Alert>
            )}

            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>On the PDF</TableCell>
                  <TableCell sx={{ width: 360 }}>{newPo ? "SKU" : "SKU on this PO"}</TableCell>
                  <TableCell align="right">{newPo ? "" : "PO now"}</TableCell>
                  <TableCell align="right">PDF qty</TableCell>
                  <TableCell align="right">PDF price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {extraction.lines.map((line, index) => {
                  const chosen = matches[index] ?? null;
                  const existing = chosen ? currentByInventoryId.get(chosen) : undefined;
                  const changed = chosen != null && (existing == null || existing.quantity !== line.quantity);
                  return (
                    <TableRow key={index} sx={plan?.duplicates.has(chosen ?? "") ? { bgcolor: "error.light" } : undefined}>
                      <TableCell>
                        {line.productNumber && <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.productNumber}</Typography>}
                        <Typography variant="body2" color={line.productNumber ? "text.secondary" : undefined}>{line.description}</Typography>
                        {!chosen && line.suggestions.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            closest: {line.suggestions[0].label} ({Math.round(line.suggestions[0].score * 100)}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={inventoryOptions}
                          getOptionLabel={(opt) => labelFor(opt.id)}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) {
                              // Ranked suggestions first when nothing is typed
                              const ids = line.suggestions.map(sug => String(sug.itemId));
                              return [
                                ...ids.map(id => optionById.get(id)).filter((opt): opt is InventoryOption => Boolean(opt)),
                                ...options.filter(opt => !ids.includes(opt.id)),
                              ];
                            }
                            const lower = inputValue.toLowerCase();
                            return options.filter(opt =>
                              opt.id.toLowerCase().includes(lower)
                              || (opt.name && opt.name.toLowerCase().includes(lower))
                              || opt.skus.some(sku => sku.toLowerCase().includes(lower))
                              || (suggestedProductNumberFor(opt.id) ?? "").toLowerCase().includes(lower));
                          }}
                          value={chosen ? (optionById.get(chosen) ?? { id: chosen, name: null, skus: [] }) : null}
                          onChange={(_, value) => setMatches(prev => ({ ...prev, [index]: value?.id ?? null }))}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search by name, SKU, inventory id or Danican #…"
                              error={!chosen}
                              helperText={chosen && line.suggestions[0] && String(line.suggestions[0].itemId) === chosen
                                ? `matched by ${line.suggestions[0].matchedBy === "manufacturer_sku" ? "product #" : line.suggestions[0].matchedBy === "inventory_id" ? "inventory id" : "description"}`
                                : undefined}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">{newPo ? "" : existing ? existing.quantity : chosen ? "not on PO" : "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: changed ? 700 : 400 }}>{line.quantity}</TableCell>
                      <TableCell align="right">{line.unitPrice != null ? `$${line.unitPrice.toFixed(2)}` : "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {plan && plan.untouched.length > 0 && (
              <Alert severity="warning">
                <strong>Not on the PDF, left as they are:</strong> {plan.untouched.map(li => li.inventoryName || li.inventoryId).join(", ")}.
                Delete them from the PO by hand if they should not be there.
              </Alert>
            )}
            </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }} disabled={isReading}>Cancel</Button>
        {!extraction ? (
          <Button variant="contained" onClick={() => handleRead()} disabled={!file || isReading}>
            {isReading ? "Reading…" : "Read PDF"}
          </Button>
        ) : (
          <>
            <Button onClick={reset}>Choose another PDF</Button>
            <Button variant="contained" onClick={handleApply} disabled={!canApply}>
              {newPo ? "Create PO with these lines" : mode === "prices" ? "Apply prices & quantities" : "Apply to PO lines"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
